import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api/auth';
import { ServiceApplicationResponse, ServiceApplicationStatus, ServiceResponse } from '../types';
import { Send, Clock3, CheckCircle2, XCircle } from 'lucide-react';

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const getApplicationStatusLabel = (status: ServiceApplicationStatus) => {
  if (status === 'APPROVED') return '승인됨';
  if (status === 'REJECTED') return '거절됨';
  return '대기중';
};

const getApplicationStatusBadgeClass = (status: ServiceApplicationStatus) => {
  if (status === 'APPROVED') return 'badge-active';
  if (status === 'REJECTED') return 'badge-locked';
  return 'badge-inactive';
};

const ServiceApplicationsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.globalRole === 'ROOT_ADMIN';

  const [selectedServiceCode, setSelectedServiceCode] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [services, setServices] = useState<ServiceResponse[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState('');
  const [myApplications, setMyApplications] = useState<ServiceApplicationResponse[]>([]);
  const [adminApplications, setAdminApplications] = useState<ServiceApplicationResponse[]>([]);

  const accessibleServiceCodes = useMemo(
    () => new Set((user?.services ?? []).map((service) => service.code)),
    [user?.services]
  );

  const requestableServices = useMemo(
    () =>
      services.filter(
        (service) => service.status === 'ACTIVE' && !accessibleServiceCodes.has(service.code)
      ),
    [accessibleServiceCodes, services]
  );

  const loadServices = useCallback(async () => {
    if (isAdmin) return;

    setServicesLoading(true);
    setServicesError('');
    try {
      const res = await authApi.getServiceApplicationCatalog();
      setServices((res.data.result || []).filter((service) => service.status === 'ACTIVE'));
    } catch {
      setServices([]);
      setServicesError('신청할 수 있는 서비스 목록을 불러오지 못했습니다.');
    } finally {
      setServicesLoading(false);
    }
  }, [isAdmin]);

  const loadApplications = useCallback(async () => {
    try {
      if (isAdmin) {
        const res = await authApi.getServiceApplications();
        setAdminApplications(res.data.result || []);
      } else {
        const res = await authApi.getMyServiceApplications();
        setMyApplications(res.data.result || []);
      }
    } catch {
      if (isAdmin) {
        setAdminApplications([]);
      } else {
        setMyApplications([]);
      }
    }
  }, [isAdmin]);

  useEffect(() => {
    loadServices();
    loadApplications();
  }, [loadApplications, loadServices]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedServiceCode.trim()) return;

    setRequestLoading(true);
    try {
      await authApi.submitServiceApplication({ serviceCode: selectedServiceCode.trim().toLowerCase() });
      setSelectedServiceCode('');
      await loadApplications();
    } catch {
      alert('서비스 신청에 실패했습니다.');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleApplicationUpdate = async (applicationId: number, status: ServiceApplicationStatus) => {
    try {
      await authApi.updateServiceApplication(applicationId, { status });
      await loadApplications();
    } catch {
      alert('신청 상태를 변경하지 못했습니다.');
    }
  };

  const applications = isAdmin ? adminApplications : myApplications;

  return (
    <div className="fade-in">
      <section className="card dashboard-hero-card" style={{ marginBottom: '1rem' }}>
        <div className="dashboard-hero-grid">
          <div className="dashboard-hero-copy">
            <div className="dashboard-page-kicker">서비스 신청</div>
            <h1 className="dashboard-hero-title" style={{ fontSize: '1.55rem' }}>
              {isAdmin ? '서비스 신청을 검토합니다.' : '원하는 서비스를 신청하세요.'}
            </h1>
            <p className="dashboard-hero-description">
              {isAdmin
                ? '사용자가 요청한 서비스 접근을 승인하거나 거절할 수 있습니다.'
                : '승인된 계정만 신청할 수 있으며, 이미 사용 중인 서비스는 제외됩니다.'}
            </p>
          </div>

          <div className="dashboard-hero-stats" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div className="dashboard-stat-chip">
              <span>{isAdmin ? '전체 신청' : '내 신청'}</span>
              <strong>{applications.length}</strong>
            </div>
            <div className="dashboard-stat-chip">
              <span>대기중</span>
              <strong>{applications.filter((application) => application.status === 'PENDING').length}</strong>
            </div>
          </div>
        </div>
      </section>

      {isAdmin ? (
          <section className="card dashboard-main-card">
            <div className="dashboard-card-head">
              <div className="dashboard-card-kicker">
                <Clock3 size={14} />
                <span>신청 목록</span>
              </div>
            <h2 className="dashboard-card-title dashboard-card-title-sm">대기 중인 신청을 처리하세요.</h2>
            </div>

          <div className="dashboard-admin-toolbar">
            <button className="btn btn-outline" type="button" onClick={loadApplications}>
              새로고침
            </button>
          </div>

          <div className="dashboard-application-list">
            {applications.length > 0 ? (
              applications.map((application) => (
                <div key={application.id} className="dashboard-application-item">
                  <div className="dashboard-application-top">
                    <strong>{application.serviceName || application.serviceCode}</strong>
                    <span className={`badge ${getApplicationStatusBadgeClass(application.status)}`}>
                      {getApplicationStatusLabel(application.status)}
                    </span>
                  </div>
                  <div className="dashboard-application-meta">
                    <span>{application.requesterName || application.requesterEmail || '-'}</span>
                    <span>{formatDate(application.createdAt)}</span>
                  </div>
                  {application.status === 'PENDING' && (
                    <div className="dashboard-admin-approval">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleApplicationUpdate(application.id, 'APPROVED')}
                      >
                        <CheckCircle2 size={16} /> 승인
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => handleApplicationUpdate(application.id, 'REJECTED')}
                      >
                        <XCircle size={16} /> 거절
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="dashboard-empty-note">신청 내역이 없습니다.</div>
            )}
          </div>
        </section>
      ) : (
        <div className="dashboard-grid dashboard-grid-clean">
          <section className="card dashboard-main-card">
            <div className="dashboard-card-head">
              <div className="dashboard-card-kicker">
                <Send size={14} />
                <span>서비스 접근 신청</span>
              </div>
              <h2 className="dashboard-card-title dashboard-card-title-sm">신청할 서비스를 선택하세요.</h2>
            </div>

            <form onSubmit={handleSubmit} className="dashboard-request-form dashboard-request-inline">
              <div className="dashboard-request-field">
                <label className="label">서비스 선택</label>
                <select
                  className="input"
                  value={selectedServiceCode}
                  onChange={(e) => setSelectedServiceCode(e.target.value)}
                  required
                  disabled={servicesLoading}
                >
                  <option value="">
                    {servicesLoading ? '서비스 목록을 불러오는 중...' : '신청할 서비스를 선택하세요'}
                  </option>
                  {requestableServices.map((service) => (
                    <option key={service.code} value={service.code}>
                      {service.name} ({service.code})
                    </option>
                  ))}
                </select>
                <div className="dashboard-request-helper">
                  {servicesError
                    ? servicesError
                    : requestableServices.length > 0
                    ? '이미 접근 가능한 서비스는 제외됩니다.'
                    : servicesLoading
                      ? '서비스 목록을 불러오고 있습니다.'
                      : '신청 가능한 서비스가 없습니다.'}
                </div>
              </div>
              <button className="btn btn-primary dashboard-request-submit" type="submit" disabled={requestLoading}>
                {requestLoading ? '신청 중...' : '신청하기'}
              </button>
            </form>
          </section>

          <aside className="dashboard-side-stack">
            <section className="card dashboard-side-card">
              <div className="dashboard-card-kicker">
                <Clock3 size={14} />
                <span>내 신청</span>
              </div>
              <div className="dashboard-application-list">
                {applications.length > 0 ? (
                  applications.map((application) => (
                    <div key={application.id} className="dashboard-application-item">
                  <div className="dashboard-application-top">
                    <strong>{application.serviceName || application.serviceCode}</strong>
                    <span className={`badge ${getApplicationStatusBadgeClass(application.status)}`}>
                      {getApplicationStatusLabel(application.status)}
                    </span>
                  </div>
                      <div className="dashboard-application-meta">
                        <span>{formatDate(application.createdAt)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="dashboard-empty-note">신청 내역이 없습니다.</div>
                )}
              </div>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
};

export default ServiceApplicationsPage;
